import { Box, Input, Typography, useMediaQuery } from '@mui/material'
import { isEmpty, isNil, replace, split } from 'lodash/fp'
import React from 'react'
import {
  type InputProps,
  type TextInputProps,
  useInput,
  useTranslate
} from 'react-admin'
import { useFormState, useFormContext } from 'react-hook-form'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import ja from 'date-fns/locale/ja'
import { format, parse, isValid, parseISO } from 'date-fns/fp'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import log from 'loglevel'

log.setLevel(log.levels.DEBUG)

const ValidateError: React.FC<{
  errorMessage: string
}> = ({ errorMessage }) => {
  const translate = useTranslate()
  return (
    <Typography variant="subtitle2" color="red" sx={{ marginLeft: '1em' }}>
      {translate(errorMessage)}
    </Typography>
  )
}

export const TextInput: React.FC<
  TextInputProps & {
    source: string
    placeholder: string
    fields?: string
    validate?: any
    label?: string
  }
> = ({ source, placeholder, validate, fields, label, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { field, isRequired } = useInput({ source, validate, ...props })
  const translate = useTranslate()
  const name = replace(/\.\d/, '')(field.name)
  const labelString = isNil(label)
    ? translate(`${fields as string}.${name}`)
    : label
  const { errors } = useFormState()
  let error: any = errors
  if (!isNil(error)) {
    for (const field of split('.')(source)) {
      error = error[field]
      if (isNil(error)) break
    }
  }
  return (
    <>
      <Typography
        variant="subtitle1"
        sx={{ marginLeft: isDesktop ? '0.50em' : '0.25em' }}>
        {labelString}
      </Typography>
      <Box
        border={1}
        sx={{
          height: '2em',
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em',
          marginLeft: isDesktop ? '0.50em' : '0.25em',
          borderRadius: '10px',
          borderColor: '#c5c5c5'
        }}>
        <Input
          {...field}
          placeholder={placeholder}
          fullWidth
          disableUnderline
          sx={{ marginLeft: '1em', fontVariant: 'body1' }}
          required={isRequired}
        />
      </Box>
      {!isNil(error) && <ValidateError errorMessage={error.message} />}
    </>
  )
}

export const DateInput: React.FC<
  InputProps & {
    source: string
    fields: string
    disableFuture?: boolean
    disablePast?: boolean
    alwaysOn?: boolean
    validate?: any
  }
> = ({
  source,
  disableFuture,
  disablePast,
  alwaysOn,
  validate,
  fields,
  ...props
}) => {
  const translate = useTranslate()
  const { disabled } = props
  const { field, isRequired } = useInput({ source, ...props })
  const { value } = field
  const [dateValue, setDateValue] = React.useState<Date | null>(
    !isNil(value) && !isEmpty(value) ? parseISO(value) : null
  )
  const { setValue } = useFormContext()
  const label = translate(`${fields}.${replace(/\.[0-9]+/g, '')(field.name)}`)
  React.useEffect(() => {
    setDateValue(!isNil(value) && !isEmpty(value) ? parseISO(value) : null)
  }, [value])
  return (
    <>
      <Typography variant="subtitle1">{label}</Typography>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={ja}
        localeText={{
          cancelButtonLabel: 'キャンセル',
          okButtonLabel: '選択'
        }}>
        <DatePicker
          value={dateValue}
          format="yyyy-MM-dd"
          onChange={(newValue: Date | null): void => {
            if (isValid(newValue)) {
              const value = !isNil(newValue)
                ? format('yyyy-MM-dd')(newValue)
                : ''
              setDateValue(newValue)
              setValue(source, value)
              field.onChange(value)
            }
          }}
          slotProps={{
            field: {
              clearable: true,
              onClear: (): void => {
                setValue(source, null)
                setDateValue(null)
                field.onChange(null)
              },
              format: 'yyyy年M月d日'
            },
            textField: {
              required: isRequired,
              sx: {
                borderRadius: '10px',
                borderColor: '#c5c5c5'
              },
              variant: 'outlined',
              fullWidth: true,
              size: 'medium',
              inputProps: { style: { fontVariant: 'body1' } },
              InputLabelProps: { disabled: true },
              hiddenLabel: true
            },
            toolbar: {
              hidden: true
            }
          }}
          views={['year', 'month', 'day']}
          minDate={parse(new Date(), 'yyyy-MM-dd')('2023-01-01')}
          disableFuture={!isNil(disableFuture) ? disableFuture : false}
          disablePast={!isNil(disablePast) ? disablePast : false}
          disabled={disabled ?? false}
        />
      </LocalizationProvider>
    </>
  )
}
export const useScroll = (obj?: any): React.RefObject<HTMLDivElement> => {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  React.useLayoutEffect(() => {
    scrollRef?.current?.scrollIntoView({
      block: 'center'
    })
  }, [])
  React.useEffect(() => {
    if (!isNil(obj)) {
      scrollRef?.current?.scrollIntoView({
        block: 'center'
      })
    }
  }, [obj])
  return scrollRef
}
